import { MutationKey, useMutationState, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, Suspense, useEffect } from 'react';
import { z } from 'zod';

import {
  FilterSchema,
  ParagraafSchemaObjectStatus,
  SubparagraafSchemaObjectStatus,
  VoorschriftSchemaObjectStatus,
} from '@/api';
import { useBesluitId } from '@/api/queries/besluit';
import { useGebruiker } from '@/api/queries/gebruiker/useGebruiker';
import {
  useHoofdstukReservering,
  useHoofdstukReserveringByLineageId,
} from '@/api/queries/hoofdstukReservering';
import {
  LinkParagraaf,
  LinkSubparagraaf,
  useHoofdstukHB,
  useLinksInHoofdstuk,
  useOrderParagraafLink,
  useOrderSubparagraafLink,
  useOrderVoorschriftLink,
  useParagraafId,
  useSubparagraafId,
  useVoorschriftId,
} from '@/api/queries/objects';
import {
  ensureQueryDataHoofdstukByLineageId,
  useHoofdstukByLineageId,
} from '@/api/queries/objects/selectors/useHoofdstukByLineageId';
import { useGrondslagHasStandaardteksten } from '@/api/queries/std/selectors/useGrondslagHasStandaardteksten';
import { Divider } from '@/components/Divider/Divider';
import {
  DraggableEditor,
  DraggableParagraaf,
  DraggableSubparagraaf,
  DraggableVoorschrift,
  DroppableParagraaf,
  DroppableSubparagraaf,
  DroppableVoorschrift,
} from '@/components/DraggableEditor';
import { DropdownMenu } from '@/components/DropdownMenu/DropdownMenu';
import { ModalTrigger } from '@/components/Modal';
import { Warning } from '@/components/Warning/Warning';
import { EASING } from '@/constants/easing';
import { SolidSpinner } from '@/icons/components';
import { JumpToParagraafFetch } from '@/modals/JumpToParagraaf';
import { useUserStore } from '@/stores/user';
import { useOffsetTop } from '@/utils/hooks/useOffsetTop';
import { twJoin } from '@/utils/tw-merge';

import { Hoofdstuk } from './-components/Hoofdstuk/Hoofdstuk';
import { HoofdstukAddBar } from './-components/Hoofdstuk/HoofdstukAddBar';
import {
  HoofdstukProvider,
  useHoofdstukContext,
} from './-components/Hoofdstuk/hoofdstukContext';
import { Paragraaf } from './-components/Paragraaf/Paragraaf';
import { ParagraafAddBar } from './-components/Paragraaf/ParagraafAddBar';
import { ParagraafSkeleton } from './-components/Paragraaf/ParagraafSkeleton';
import { SidebarRight } from './-components/SidebarRight/SidebarRight';
import { Slot } from './-components/Slot/Slot';
import { Subparagraaf } from './-components/Subparagraaf/Subparagraaf';
import { SubparagraafAddBar } from './-components/Subparagraaf/SubparagraafAddBar';
import { Toolbar } from './-components/Toolbar/Toolbar';
import { Voorschrift } from './-components/Voorschrift/Voorschrift';
import { VoorschriftAddBar } from './-components/Voorschrift/VoorschriftAddBar';
import {
  EDITOR_STORE_INITIAL_STATE,
  EditorView,
  useEditorStore,
} from './-store/useEditorStore';
import { getObjectState } from './-utils/getObjectState';
import { useAutoExtendReservering } from './-utils/useAutoExtendReservering';

const getLinksQueryOptions = (
  {
    hoofdstukId,
    besluitId,
    view,
  }: {
    hoofdstukId: string;
    besluitId: string;
    view: EditorView;
  },
  options: { enabled?: boolean } = {}
) => {
  const VSPBFilter: FilterSchema[] = [
    { model: 'Voorschrift', field: 'Volgend', op: 'is_null', value: '' },
  ];

  const SPPBFilter: FilterSchema[] = [
    { model: 'Subparagraaf', field: 'Volgend', op: 'is_null', value: '' },
  ];

  const PHBFilter: FilterSchema[] = [
    { model: 'Paragraaf', field: 'Volgend', op: 'is_null', value: '' },
  ];

  if (view !== 'recover') {
    VSPBFilter.push({
      model: 'Voorschrift',
      field: 'ObjectStatus',
      op: 'ne',
      value: VoorschriftSchemaObjectStatus.IngetrokkenGepubliceerd,
    });

    SPPBFilter.push({
      model: 'Subparagraaf',
      field: 'ObjectStatus',
      op: 'ne',
      value: SubparagraafSchemaObjectStatus.IngetrokkenGepubliceerd,
    });

    PHBFilter.push({
      model: 'Paragraaf',
      field: 'ObjectStatus',
      op: 'ne',
      value: ParagraafSchemaObjectStatus.IngetrokkenGepubliceerd,
    });
  }

  return useLinksInHoofdstuk.getOptions(
    {
      hoofdstukId,
      besluitId,
      VSPBFilter,
      SPPBFilter,
      PHBFilter,
    },
    options
  );
};

const REORDERING_MUTATION_KEY: MutationKey = ['reordering'];

const searchSchema = z.object({
  recovered: z.boolean().optional(),
});

export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/voorschriften/$hoofdstukLineageId'
)({
  validateSearch: searchSchema.parse,
  loader: async ({
    context: { queryClient },
    params: { hoofdstukLineageId, besluitId },
  }) => {
    const [hoofdstuk] = await Promise.all([
      ensureQueryDataHoofdstukByLineageId(hoofdstukLineageId, queryClient),
      // Preload the users, these are used in the sidebar
      queryClient.ensureQueryData(useGebruiker.getOptions('')),
    ]);

    if (!hoofdstuk?.ID) return;

    const [links] = await Promise.all([
      queryClient.ensureQueryData(
        getLinksQueryOptions({
          hoofdstukId: hoofdstuk.ID,
          besluitId,
          view: EDITOR_STORE_INITIAL_STATE.view,
        })
      ),
      queryClient.ensureQueryData(
        useHoofdstukHB.getOptions({ hoofdstukId: hoofdstuk.ID, besluitId })
      ),
      useBesluitId.ensureQueryData(besluitId),
    ]);

    /* Preload first subparagraaf */
    const firstSubparagraaf = links[0]?.subparagrafen[0];
    if (firstSubparagraaf) {
      await Promise.all([
        useParagraafId.ensureQueryData(
          firstSubparagraaf.paragraaf.PHB.Paragraaf
        ),
        useSubparagraafId.ensureQueryData(firstSubparagraaf.SPPB.Subparagraaf),
        ...firstSubparagraaf.voorschriften.map((voorschrift) =>
          useVoorschriftId.ensureQueryData(voorschrift.VSPB.Voorschrift)
        ),
      ]);
    }
  },
  component: VoorschriftenHoofdstukComponent,
  onEnter: ({ context: { queryClient }, search }) => {
    if (search.recovered) {
      useEditorStore.getState().setView('recover');
    }

    queryClient?.invalidateQueries({ queryKey: useHoofdstukReservering.key });
  },
  onLeave: () => {
    useEditorStore.getState().setView(EDITOR_STORE_INITIAL_STATE.view);
    useEditorStore.getState().clearSelectedObject();
  },
});

function VoorschriftenHoofdstukComponent() {
  const { hoofdstukLineageId, besluitId } = Route.useParams();
  const view = useEditorStore((state) => state.view);
  const setView = useEditorStore((state) => state.setView);

  const hoofdstuk = useHoofdstukByLineageId(hoofdstukLineageId);

  const links = useQuery(
    getLinksQueryOptions(
      { hoofdstukId: hoofdstuk.data?.ID ?? '', besluitId, view },
      { enabled: !!hoofdstuk.data?.ID }
    )
  );

  /**
   * Check if user has reservering
   */
  const hoofdstukReservering =
    useHoofdstukReserveringByLineageId(hoofdstukLineageId);
  const userId = useUserStore((state) => state.user?.ID);

  const userHasReservering = hoofdstukReservering.data?.Gebruiker === userId;

  /**
   * Check if the current besluit is an initieel besluit
   */
  const besluit = useBesluitId(besluitId);

  /**
   * Check if current hoofdstuk is locked
   */
  const hb = useHoofdstukHB({
    hoofdstukId: hoofdstuk.data?.ID ?? '',
    besluitId,
  });

  /**
   * Check if the current grondslag has standaardteksten
   */
  const { data: grondslagHasStandaardteksten } =
    useGrondslagHasStandaardteksten(hoofdstuk.data?.Grondslag);

  /**
   * Auto extend hoofdstuk reservering
   */
  useAutoExtendReservering(hoofdstukLineageId);

  /**
   * If the besluit is bevroren, set the view to default
   */
  useEffect(() => {
    if (besluit.data?.Status === 'Bevroren') {
      setView('default');
    }
  }, [besluit.data?.Status, setView]);

  /**
   * Paragrafen
   */
  const { ref: listRef, offsetTop } = useOffsetTop();
  const virtualizer = useWindowVirtualizer({
    count: links.data?.length ?? 0,
    estimateSize: () => 1000,
    overscan: 1,
    enabled: !!links.data?.length,
    scrollMargin: offsetTop,
    paddingEnd: 300,
  });

  /**
   * Drag handlers for paragraaf
   */
  const orderParagraafLink = useOrderParagraafLink({
    mutationKey: REORDERING_MUTATION_KEY,
  });

  if (!links.data || !hoofdstuk.data || !hb.data || !besluit.data) return null;

  const onDropParagraaf = ({
    from,
    to,
    method,
  }: {
    from: number;
    to: number;
    method: 'prepend' | 'append';
  }) => {
    orderParagraafLink.mutate({
      from,
      to,
      method,
      links: links.data,
    });
  };

  const hoofdstukState = getObjectState(hoofdstuk.data);
  const isHoofdstukNew = hoofdstukState === 'new';

  const virtualItems = virtualizer.getVirtualItems();

  if (hoofdstuk.data.ObjectStatus === 'IngetrokkenGepubliceerd') {
    return (
      <div className="container-default relative mx-auto min-h-screen px-4">
        <Warning size="large" variant="warning" className="mt-[10rem]">
          <Warning.Title>
            {hoofdstuk.data.Nummer}.{' '}
            <span dangerouslySetInnerHTML={{ __html: hoofdstuk.data.Titel }} />
          </Warning.Title>

          <Warning.Description>
            Dit hoofdstuk is ingetrokken en gepubliceerd. Open het menu om een
            ander hoofdstuk te bekijken.
          </Warning.Description>
        </Warning>
      </div>
    );
  }

  const hoofdstukNummer = hoofdstuk.data.Nummer;

  return (
    <HoofdstukProvider
      hoofdstuk={hoofdstuk.data}
      hb={hb.data}
      userHasReservering={userHasReservering}
      besluit={besluit.data}
      grondslagHasStandaardteksten={!!grondslagHasStandaardteksten}
      links={links.data}
      hoofdstukState={hoofdstukState}
    >
      <div className="container-default relative mx-auto min-h-screen px-4 pt-4">
        <ReorderingOverlay />

        <Toolbar
          optionsMenuContent={
            <ModalTrigger
              modalComponent={JumpToParagraafFetch}
              onOpen={() => ({
                onClick: ({ index }) => {
                  virtualizer.scrollToIndex(index, { align: 'start' });
                },
                paragraafIds: links.data.map((link) => link.PHB.Paragraaf),
                hoofdstukNummer,
                stateIconOptions: {
                  besluitTypeId: besluit.data.Type,
                },
              })}
              asChild
            >
              <DropdownMenu.Button disabled={links.data.length === 0}>
                Alle paragrafen
              </DropdownMenu.Button>
            </ModalTrigger>
          }
        />

        <DraggableEditor allowReparenting={isHoofdstukNew}>
          <div
            className="relative w-full"
            style={{
              height: `${virtualizer.getTotalSize()}px`,
            }}
            ref={listRef}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                paddingTop: virtualItems.length ? 0 : `${offsetTop}px`,
                transform: `translateY(${(virtualItems[0]?.start ?? 0) - virtualizer.options.scrollMargin}px)`,
              }}
            >
              <Hoofdstuk />

              {!links.data.length && view === 'add' && (
                <ParagraafAddBar className="mb-2" testId="add-bar-0" />
              )}

              {virtualizer.getVirtualItems().map((item) => {
                const paragraafLink = links.data[item.index]!;
                const testId = `paragraaf-${item.index}`;

                return (
                  <div key={item.key}>
                    {view === 'dragndrop' && (
                      <DroppableParagraaf
                        index={item.index}
                        method="prepend"
                        onDrop={(from) => {
                          onDropParagraaf({
                            from: from.index,
                            to: item.index,
                            method: 'prepend',
                          });
                        }}
                      />
                    )}
                    {view === 'add' && (
                      <ParagraafAddBar
                        className="mb-2"
                        previous={links.data[item.index - 1]}
                        next={paragraafLink}
                        testId={`add-bar-${item.index}`}
                      />
                    )}

                    <DraggableParagraaf index={item.index}>
                      {({ dragHandleRef, dragPreviewRef }) => (
                        <Suspense
                          fallback={
                            <ParagraafSkeleton
                              ref={virtualizer.measureElement}
                              data-index={item.index}
                              link={paragraafLink}
                              testId={testId}
                            />
                          }
                        >
                          <Paragraaf
                            ref={virtualizer.measureElement}
                            data-index={item.index}
                            link={paragraafLink}
                            isSelectable={view !== 'dragndrop'}
                            testId={testId}
                            dragHandleRef={dragHandleRef}
                            dragPreviewRef={dragPreviewRef}
                          >
                            <Subparagrafen
                              paragraafLink={paragraafLink}
                              paragraafIndex={item.index}
                            />
                          </Paragraaf>
                        </Suspense>
                      )}
                    </DraggableParagraaf>

                    {item.index === links.data.length - 1 &&
                      view === 'dragndrop' && (
                        <DroppableParagraaf
                          method="append"
                          index={item.index}
                          onDrop={(from) => {
                            onDropParagraaf({
                              from: from.index,
                              to: item.index,
                              method: 'append',
                            });
                          }}
                        />
                      )}

                    {item.index === links.data.length - 1 && view === 'add' && (
                      <ParagraafAddBar
                        className="mb-2"
                        previous={links.data[links.data.length - 1]}
                        testId={`add-bar-${links.data.length}`}
                      />
                    )}
                  </div>
                );
              })}

              {view === 'add' && (
                <>
                  <Divider className="mb-2" />
                  <HoofdstukAddBar />
                </>
              )}
            </div>
          </div>
        </DraggableEditor>
      </div>

      {hb.data.Type === 'MBIO' && (
        <Slot
          type="hoofdstuk"
          state={hoofdstukState}
          id={hoofdstuk.data.ID ?? ''}
          messageClassName="mt-[10rem]"
          className="fixed inset-0"
        />
      )}

      <SidebarRight
        hoofdstuk={hoofdstuk.data}
        hb={hb.data}
        links={links.data}
        userHasReservering={userHasReservering}
      />
    </HoofdstukProvider>
  );
}

const Subparagrafen = ({
  paragraafLink,
  paragraafIndex,
}: {
  paragraafLink: LinkParagraaf;
  paragraafIndex: number;
}) => {
  const { links } = useHoofdstukContext();

  const view = useEditorStore((state) => state.view);

  const orderSubparagraafLink = useOrderSubparagraafLink({
    mutationKey: REORDERING_MUTATION_KEY,
  });

  const onDropSubparagraaf = ({
    from,
    to,
    method,
  }: {
    from: [number, number];
    to: [number, number];
    method: 'prepend' | 'append';
  }) => {
    orderSubparagraafLink.mutate({
      from,
      to,
      method,
      links,
    });
  };

  if (!paragraafLink.subparagrafen.length && view === 'add') {
    return (
      <SubparagraafAddBar
        paragraafLink={paragraafLink}
        testId={`add-bar-${paragraafIndex}-0`}
      />
    );
  }

  return (
    <div className={twJoin('flex flex-col', view !== 'dragndrop' && 'gap-2')}>
      {paragraafLink.subparagrafen.map(
        (subparagraafLink, subparagraafIndex) => {
          const path: [number, number] = [paragraafIndex, subparagraafIndex];

          return (
            <Fragment key={`subparagraaf-${subparagraafLink.SPPB.ID}`}>
              <DraggableSubparagraaf path={path}>
                {({ dragHandleRef, dragPreviewRef }) => (
                  <Subparagraaf
                    key={`subparagraaf-${subparagraafLink.SPPB.ID}`}
                    link={subparagraafLink}
                    testId={`subparagraaf-${paragraafIndex}-${subparagraafIndex}`}
                    dragHandleRef={dragHandleRef}
                    dragPreviewRef={dragPreviewRef}
                  >
                    <Voorschriften
                      subparagraafIndex={subparagraafIndex}
                      paragraafIndex={paragraafIndex}
                      subparagraafLink={subparagraafLink}
                    />
                  </Subparagraaf>
                )}
              </DraggableSubparagraaf>

              {view === 'dragndrop' && (
                <DroppableSubparagraaf
                  method="append"
                  path={path}
                  onDrop={(from) => {
                    onDropSubparagraaf({
                      from,
                      to: path,
                      method: 'append',
                    });
                  }}
                />
              )}

              {view === 'add' && (
                <SubparagraafAddBar
                  paragraafLink={paragraafLink}
                  previous={subparagraafLink}
                  next={paragraafLink.subparagrafen[subparagraafIndex + 1]}
                  testId={`add-bar-${paragraafIndex}-${subparagraafIndex}`}
                />
              )}
            </Fragment>
          );
        }
      )}
    </div>
  );
};

const Voorschriften = ({
  subparagraafLink,
  paragraafIndex,
  subparagraafIndex,
}: {
  subparagraafLink: LinkSubparagraaf;
  paragraafIndex: number;
  subparagraafIndex: number;
}) => {
  const { links, hoofdstukState } = useHoofdstukContext();
  const orderVoorschriftLink = useOrderVoorschriftLink({
    mutationKey: REORDERING_MUTATION_KEY,
  });

  const view = useEditorStore((state) => state.view);

  const onDropVoorschrift = ({
    from,
    to,
    method,
  }: {
    from: [number, number, number];
    to: [number, number, number];
    method: 'prepend' | 'append';
  }) => {
    orderVoorschriftLink.mutate({
      from,
      to,
      method,
      links,
    });
  };

  if (view === 'add' && !subparagraafLink.voorschriften.length) {
    return (
      <VoorschriftAddBar
        subparagraafLink={subparagraafLink}
        testId={`add-bar-${paragraafIndex}-${subparagraafIndex}-0`}
      />
    );
  }

  if (
    view === 'dragndrop' &&
    !subparagraafLink.voorschriften.length &&
    hoofdstukState === 'new'
  ) {
    return (
      <DroppableVoorschrift
        path={[paragraafIndex, subparagraafIndex, 0]}
        method="prepend"
        onDrop={(from) => {
          onDropVoorschrift({
            from,
            to: [paragraafIndex, subparagraafIndex, 0],
            method: 'prepend',
          });
        }}
      />
    );
  }

  return (
    <div className={twJoin('flex flex-col', view !== 'dragndrop' && 'gap-1')}>
      {subparagraafLink.voorschriften.map(
        (voorschriftLink, voorschriftIndex) => {
          return (
            <Fragment
              key={`${voorschriftLink.VSPB.Subparagraaf}-${voorschriftLink.VSPB.Voorschrift}`}
            >
              {view === 'add' && (
                <VoorschriftAddBar
                  subparagraafLink={subparagraafLink}
                  previous={
                    subparagraafLink.voorschriften[voorschriftIndex - 1]
                  }
                  next={subparagraafLink.voorschriften[voorschriftIndex]}
                  testId={`add-bar-${paragraafIndex}-${subparagraafIndex}-${voorschriftIndex}`}
                />
              )}

              {view === 'dragndrop' && (
                <DroppableVoorschrift
                  method="prepend"
                  path={[paragraafIndex, subparagraafIndex, voorschriftIndex]}
                  onDrop={(from) => {
                    onDropVoorschrift({
                      from,
                      to: [paragraafIndex, subparagraafIndex, voorschriftIndex],
                      method: 'prepend',
                    });
                  }}
                />
              )}

              <DraggableVoorschrift
                path={[paragraafIndex, subparagraafIndex, voorschriftIndex]}
              >
                {({ dragHandleRef, dragPreviewRef }) => (
                  <Voorschrift
                    dragHandleRef={dragHandleRef}
                    dragPreviewRef={dragPreviewRef}
                    link={voorschriftLink}
                    isSelectable={view !== 'dragndrop'}
                    testId={`voorschrift-${paragraafIndex}-${subparagraafIndex}-${voorschriftIndex}`}
                  />
                )}
              </DraggableVoorschrift>
            </Fragment>
          );
        }
      )}

      {view === 'add' && (
        <VoorschriftAddBar
          subparagraafLink={subparagraafLink}
          previous={
            subparagraafLink.voorschriften[
              subparagraafLink.voorschriften.length - 1
            ]
          }
          testId={`add-bar-${paragraafIndex}-${subparagraafIndex}-${subparagraafLink.voorschriften.length}`}
        />
      )}

      {view == 'dragndrop' && (
        <DroppableVoorschrift
          method="append"
          path={[
            paragraafIndex,
            subparagraafIndex,
            subparagraafLink.voorschriften.length - 1,
          ]}
          onDrop={(from) => {
            onDropVoorschrift({
              from,
              to: [
                paragraafIndex,
                subparagraafIndex,
                subparagraafLink.voorschriften.length - 1,
              ],
              method: 'append',
            });
          }}
        />
      )}
    </div>
  );
};

const ReorderingOverlay = () => {
  /**
   * Check if there's any autosave mutations going on.
   *
   * This is a workaround because useIsMutating breaks when adding standaardteksten voorschriften from different paragrafen.
   */
  const variables = useMutationState({
    filters: {
      mutationKey: REORDERING_MUTATION_KEY,
    },
    select: (mutation) => mutation.state.status === 'pending',
  });

  const isReordering = !!variables.find(Boolean);

  return (
    <AnimatePresence>
      {isReordering && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: EASING.cubicOut }}
          className="fixed inset-0 z-navigation-below flex items-center justify-center bg-gray-600/50"
        >
          <div className="flex size-5 items-center justify-center rounded-sm bg-white shadow-lg">
            <SolidSpinner className="animate-spin" size={17} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
